"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import { useRef, useEffect, useState } from "react";
import Link from "next/link";
import { collection, getDocs } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";

interface ProductItem {
    id: string;
    displayId: string; // "01", "02", etc.
    title: string;
    image: string;
    type: "image" | "custom";
    color?: string;
    link: string;
    objectFit?: "cover" | "contain";
    bgClass?: string;
    browserMockup?: boolean;
    chromeMockup?: boolean;
    mockupUrl?: string;
    mockupAlt?: string;
    mockupTone?: "green" | "gold" | "cream";
    chronelleMockup?: boolean;
}

const UI_OVERRIDES: Record<string, Partial<ProductItem>> = {
    guestiqo: {
        image: "/images/guestiqo_3phones_v5.png",
        objectFit: "contain",
        bgClass: "bg-[#faf7f2]"
    },
    memorylayer: {
        image: "/images/memorylayer_home_card.png",
        objectFit: "cover",
        bgClass: "bg-[#070B14]"
    },
    talktribe: {
        image: "/images/talktribe_product.png",
        objectFit: "contain",
        bgClass: "bg-[#060608]",
        browserMockup: true
    },
    ctl: {
        image: "/images/ctl_minimal.png",
        objectFit: "contain",
        bgClass: "bg-[#e9f0eb]",
        chromeMockup: true,
        mockupUrl: "ctlinfocom.com",
        mockupAlt: "CTL Infocom website",
        mockupTone: "green"
    },
    "crowntown-hotel": {
        image: "/images/crowntown_desktop_full.png",
        objectFit: "contain",
        bgClass: "bg-[#e8ddcc]",
        chromeMockup: true,
        mockupUrl: "crowntownhotel.com",
        mockupAlt: "Crown Town Hotel website",
        mockupTone: "gold"
    },
    "guestiqo-website": {
        image: "/images/guestiqo_website_screenshot.png",
        objectFit: "contain",
        bgClass: "bg-[#f3eee3]",
        chromeMockup: true,
        mockupUrl: "guestiqo.com",
        mockupAlt: "Guestiqo website",
        mockupTone: "cream"
    },
    chronelle: {
        image: "/images/chronelle/dashboard-clean-v3.png",
        objectFit: "contain",
        bgClass: "bg-[#17201f]",
        chronelleMockup: true
    }
};

interface ProductsProps {
    initialProducts?: ProductItem[];
}

export default function Products({ initialProducts }: ProductsProps) {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const headerContainerRef = useRef<HTMLDivElement>(null);
    const [leftSpacerWidth, setLeftSpacerWidth] = useState(0);
    const [products, setProducts] = useState<ProductItem[]>(initialProducts || []);

    // Drag to scroll state
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [activeIndex, setActiveIndex] = useState(0);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);

    // ... existing drag/wheel handlers ...

    // Drag Handlers
    const handleMouseDown = (e: React.MouseEvent) => {
        setIsDragging(true);
        setStartX(e.pageX - (scrollContainerRef.current?.offsetLeft || 0));
        setScrollLeft(scrollContainerRef.current?.scrollLeft || 0);
    };

    const handleMouseLeave = () => {
        setIsDragging(false);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.pageX - (scrollContainerRef.current?.offsetLeft || 0);
        const walk = (x - startX) * 2; // Scroll-fast
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollLeft = scrollLeft - walk;
        }
    };

    const updateScrollButtons = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            setCanScrollLeft(scrollLeft > 10);
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
        }
    };

    // Update active index on scroll
    const handleScroll = () => {
        if (scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const scrollLeft = container.scrollLeft;
            const leftSpacer = container.firstElementChild as HTMLElement;
            const spacerWidth = leftSpacer?.clientWidth || 0;

            // Find which item is closest to the left padding boundary
            let closestIndex = 0;
            let minDiff = Infinity;

            products.forEach((product, idx) => {
                const childElement = container.children[idx + 1] as HTMLElement;
                if (childElement) {
                     const diff = Math.abs(childElement.offsetLeft - spacerWidth - scrollLeft);
                     if (diff < minDiff) {
                         minDiff = diff;
                         closestIndex = idx;
                     }
                }
            });

            setActiveIndex(closestIndex);
            updateScrollButtons();
        }
    };

    const scrollToProduct = (index: number) => {
        if (scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const leftSpacer = container.firstElementChild as HTMLElement;
            const spacerWidth = leftSpacer?.clientWidth || 0;
            const childKey = container.children[index + 1] as HTMLElement;
            if (childKey) {
                container.scrollTo({
                    left: childKey.offsetLeft - spacerWidth,
                    behavior: 'smooth'
                });
            }
            setActiveIndex(index);
        }
    };

    useEffect(() => {
        if (initialProducts && initialProducts.length > 0) {
            setProducts(initialProducts);
            return;
        }
        const fetchProjects = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, "projects"));

                const fetchedProjects = querySnapshot.docs
                    .map(doc => {
                        const data = doc.data();
                        const baseSlug = (data.slug || doc.id).split('/').pop() || doc.id;
                        const isResearch = data.blocks?.some((b: any) => b.type === "research-header");
                        return {
                            id: doc.id,
                            title: data.title,
                            // Priority: Global Featured Image -> First detected image -> Placeholder
                            image: data.featuredImage || findProjectImage(data.blocks) || "/images/noise.png",
                            type: "image" as const, // For now assuming all fetched are images
                            link: `/products/${baseSlug}`,
                            isResearch,
                            visibility: data.visibility || "private",
                            createdAt: data.createdAt,
                            order: data.order
                        };
                    })
                    // Filter: Only Projects (non-Research) AND Public
                    .filter(p => !p.isResearch && p.visibility === "public");

                if (!fetchedProjects.some(project => project.id === "chronelle")) {
                    fetchedProjects.push({
                        id: "chronelle",
                        title: "Chronelle",
                        image: "/images/chronelle/dashboard-clean-v3.png",
                        type: "image" as const,
                        link: "/products/chronelle",
                        isResearch: false,
                        visibility: "public",
                        createdAt: "2026-08-07T00:00:00.000Z",
                        order: 8
                    });
                }

                // Sort by order ascending (or fallback to creation time newest first)
                fetchedProjects.sort((a, b) => {
                    const orderA = a.order !== undefined && a.order !== null && a.order !== "" ? Number(a.order) : Infinity;
                    const orderB = b.order !== undefined && b.order !== null && b.order !== "" ? Number(b.order) : Infinity;

                    if (orderA !== orderB) {
                        return orderA - orderB;
                    }

                    if (a.createdAt && b.createdAt) {
                        const aTime = a.createdAt.seconds ? a.createdAt.seconds * 1000 : new Date(a.createdAt).getTime();
                        const bTime = b.createdAt.seconds ? b.createdAt.seconds * 1000 : new Date(b.createdAt).getTime();
                        return bTime - aTime;
                    }
                    return 0;
                });

                const memoryLayerIndex = fetchedProjects.findIndex(project => project.id === "memorylayer");
                if (memoryLayerIndex >= 0) {
                    const [memoryLayer] = fetchedProjects.splice(memoryLayerIndex, 1);
                    fetchedProjects.splice(Math.min(6, fetchedProjects.length), 0, memoryLayer);
                }

                const dynamicItems: ProductItem[] = fetchedProjects.map((p, index) => {
                    const override = UI_OVERRIDES[p.id] || {};
                    return {
                        id: p.id,
                        displayId: (index + 1).toString().padStart(2, '0'),
                        title: p.title,
                        image: override.image || p.image,
                        type: "image",
                        link: p.link,
                        objectFit: override.objectFit || "cover",
                        bgClass: override.bgClass || "",
                        browserMockup: override.browserMockup || false,
                        chromeMockup: override.chromeMockup || false,
                        mockupUrl: override.mockupUrl,
                        mockupAlt: override.mockupAlt,
                        mockupTone: override.mockupTone,
                        chronelleMockup: override.chronelleMockup || false
                    };
                });

                setProducts(dynamicItems);

            } catch (error) {
                console.error("Error fetching products:", error);
            }
        };

        fetchProjects();
    }, [initialProducts]);

    useEffect(() => {
        const updateSpacerWidth = () => {
            if (headerContainerRef.current) {
                const rect = headerContainerRef.current.getBoundingClientRect();
                setLeftSpacerWidth(rect.left);
            }
        };

        const handleResize = () => {
            updateSpacerWidth();
            updateScrollButtons();
        };

        window.addEventListener("resize", handleResize);
        updateSpacerWidth();
        updateScrollButtons();

        // Extra layout pass to ensure accuracy after fonts/styles load
        const timer = setTimeout(updateSpacerWidth, 100);

        return () => {
            window.removeEventListener("resize", handleResize);
            clearTimeout(timer);
        };
    }, [products]); // Re-check when products load

    const scrollNext = () => {
        if (scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const itemWidth = (container.children[1] as HTMLElement)?.clientWidth || container.clientWidth * 0.45;
            container.scrollBy({ left: itemWidth + 32, behavior: 'smooth' }); // 32 is gap-8
        }
    };

    const scrollPrev = () => {
        if (scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const itemWidth = (container.children[1] as HTMLElement)?.clientWidth || container.clientWidth * 0.45;
            container.scrollBy({ left: -(itemWidth + 32), behavior: 'smooth' });
        }
    };

    // Helper to extract an image from blocks
    const findProjectImage = (blocks: any[]): string | null => {
        if (!blocks) return null;
        for (const block of blocks) {
            // Check Hero blocks
            if (block.type === 'hero' && block.data?.backgroundImage) return block.data.backgroundImage;
            if (block.type === 'hero-2' && block.data?.image) return block.data.image;
            if (block.type === 'hero-3' && block.data?.images?.[0]) return block.data.images[0];
            if (block.type === 'gq-web-hero' && block.data?.imageSrc) return block.data.imageSrc;

            // Check Project Gallery
            if (block.type === 'project-gallery' && block.data?.mainImage) return block.data.mainImage;

            // Check Image Grid
            if (block.type === 'image-grid' && block.data?.images?.[0]) return block.data.images[0];
        }
        return null;
    };

    // ... render return ... 

    // Update container to listen to scroll 
    {/* Horizontal Scroll Container */ }
    {/* ... products map ... */ }
    {/* Pagination Dots */ }

    return (
        <section className="pt-24 pb-8 overflow-hidden" data-header-theme="dark">
            <div className="px-6 md:px-20 lg:px-40">
                <div ref={headerContainerRef} className="container mx-auto mb-8 flex flex-col md:flex-row justify-between items-end gap-8">
                    <div className="max-w-3xl">
                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="text-[40px] md:text-[56px] font-bold leading-tight mb-6"
                        >
                            Products & Collaborations
                        </motion.h2>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.1 }}
                            className="text-lg md:text-xl opacity-60"
                        >
                            We are defined by our intentions to provide next-generation reality experiences for diverse products and brands.
                        </motion.p>
                    </div>

                    {/* Navigation Buttons */}
                    <div className="flex gap-4 shrink-0 mb-2">
                        <button
                            onClick={scrollPrev}
                            disabled={!canScrollLeft}
                            className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all ${canScrollLeft
                                ? "border-black/20 text-black hover:bg-black hover:text-white cursor-pointer"
                                : "border-black/10 text-black/20 cursor-not-allowed"
                                }`}
                            aria-label="Previous project"
                        >
                            <ChevronLeft size={20} />
                        </button>
                        <button
                            onClick={scrollNext}
                            disabled={!canScrollRight}
                            className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all ${canScrollRight
                                ? "border-black/20 text-black hover:bg-black hover:text-white cursor-pointer"
                                : "border-black/10 text-black/20 cursor-not-allowed"
                                }`}
                            aria-label="Next project"
                        >
                            <ChevronRight size={20} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Horizontal Scroll Container */}
            <div
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className={`flex gap-8 overflow-x-auto pb-4 ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                onMouseDown={handleMouseDown}
                onMouseLeave={handleMouseLeave}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
            >
                {/* Spacer for proper left padding */}
                <div 
                    className="w-6 md:w-20 lg:w-40 shrink-0 transition-all duration-300"
                    style={{ width: leftSpacerWidth ? `${leftSpacerWidth}px` : undefined }}
                ></div>

                {products.map((product, index) => (
                    <motion.div
                        key={product.id}
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        className="min-w-[85vw] md:min-w-[45vw] lg:min-w-[40vw] shrink-0"
                    >
                        <Link href={product.link} className="block w-full h-full">
                            <div className={`relative aspect-[16/10] overflow-hidden mb-6 group ${product.bgClass || ""}`}>
                                {product.type === "image" && product.browserMockup ? (
                                    <div className="absolute inset-0 flex items-center justify-center bg-[radial-gradient(circle_at_50%_42%,#251144_0%,#0b0712_52%,#050507_100%)] p-2 sm:p-4">
                                        <div className="relative flex w-[92%] flex-col items-center">
                                            <div className="relative z-10 w-full rounded-[12px] border border-zinc-700/70 bg-[#101014] p-1.5 pb-2 shadow-[0_28px_58px_rgba(0,0,0,0.75)]">
                                                <span className="absolute left-1/2 top-[3px] z-20 h-1 w-1 -translate-x-1/2 rounded-full bg-zinc-600 ring-1 ring-black" />
                                                <div className="relative aspect-[960/457] w-full overflow-hidden rounded-[7px] border border-zinc-800 bg-[#070319]">
                                                    <Image
                                                        src={product.image}
                                                        alt="TalkTribe AI character platform"
                                                        fill
                                                        sizes="(max-width: 768px) 80vw, 38vw"
                                                        className="object-contain object-center"
                                                    />
                                                    <span className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/[0.025] to-white/[0.08] pointer-events-none" />
                                                </div>
                                            </div>
                                            <div className="relative z-0 h-5 sm:h-7 w-10 sm:w-14 bg-gradient-to-r from-zinc-600 via-zinc-300 to-zinc-600 shadow-lg" />
                                            <div className="relative z-10 -mt-0.5 h-2.5 w-28 sm:w-40 rounded-[50%] bg-gradient-to-b from-zinc-300 to-zinc-600 shadow-[0_8px_16px_rgba(0,0,0,0.5)]" />
                                            <div className="absolute -bottom-3 h-7 w-[64%] rounded-full bg-black/70 blur-xl" />
                                        </div>
                                    </div>
                                ) : product.type === "image" && product.chromeMockup ? (
                                    <div className={`absolute inset-0 flex items-center justify-center p-2 sm:p-4 ${
                                        product.mockupTone === "gold"
                                            ? "bg-[radial-gradient(circle_at_50%_38%,#fbf7f0_0%,#e9ddca_56%,#cfb994_100%)]"
                                            : product.mockupTone === "cream"
                                                ? "bg-[radial-gradient(circle_at_50%_38%,#fffdf8_0%,#f1eadc_56%,#ddd1b8_100%)]"
                                                : "bg-[radial-gradient(circle_at_50%_38%,#f7fbf8_0%,#e7efe9_56%,#d5e0d8_100%)]"
                                    }`}>
                                        <div className="w-[94%] overflow-hidden rounded-[9px] sm:rounded-[13px] border border-black/20 bg-white shadow-[0_22px_50px_rgba(25,55,34,0.24)]">
                                            <div className="relative flex h-6 sm:h-8 items-center border-b border-black/10 bg-[#f1f3f2] px-2.5 sm:px-3.5">
                                                <div className="flex gap-1 sm:gap-1.5" aria-hidden="true">
                                                    <span className="h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full bg-[#ff5f57]" />
                                                    <span className="h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full bg-[#febc2e]" />
                                                    <span className="h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full bg-[#28c840]" />
                                                </div>
                                                <div className="absolute left-1/2 top-1/2 flex h-4 sm:h-5 w-[48%] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded bg-white/85 text-[5px] sm:text-[7px] font-medium tracking-wide text-zinc-500 shadow-sm">
                                                    {product.mockupUrl}
                                                </div>
                                            </div>
                                            <div className="relative aspect-video w-full overflow-hidden bg-white">
                                                <Image
                                                    src={product.image}
                                                    alt={product.mockupAlt || `${product.title} website`}
                                                    fill
                                                    sizes="(max-width: 768px) 80vw, 38vw"
                                                    className="object-cover object-top"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ) : product.type === "image" && product.chronelleMockup ? (
                                    <div className="absolute inset-0 overflow-hidden bg-[#17201f]">
                                        <Image
                                            src="/images/chronelle/card-background-v2.png"
                                            alt=""
                                            fill
                                            sizes="(max-width: 768px) 85vw, 40vw"
                                            className="object-cover"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-r from-[#101817]/20 via-transparent to-[#101817]/15" />
                                        <div className="absolute left-[7%] top-[8%] z-20">
                                            <p className="font-serif text-lg leading-none text-white sm:text-2xl">Chronelle</p>
                                            <p className="mt-1 text-[5px] uppercase tracking-[0.24em] text-[#55bfd0] sm:text-[7px]">Preserving lifetimes</p>
                                        </div>
                                        <div className="absolute inset-x-[7%] bottom-[-3%] z-10 flex h-[88%] items-end justify-center">
                                            <div className="relative h-[88%] w-[28%] translate-x-[15%] rotate-[-5deg]">
                                                <Image src="/images/chronelle/dashboard-clean-v3.png" alt="Chronelle dashboard" fill sizes="13vw" className="object-contain drop-shadow-[0_16px_18px_rgba(0,0,0,0.5)]" style={{ clipPath: "inset(0.5% 0.8% round 9% / 4.5%)" }} />
                                            </div>
                                            <div className="relative z-10 h-full w-[30%]">
                                                <Image src="/images/chronelle/recording-clean-v3.png" alt="Chronelle recording workflow" fill sizes="14vw" className="object-contain drop-shadow-[0_20px_24px_rgba(0,0,0,0.6)]" style={{ clipPath: "inset(0.5% 0.8% round 9% / 4.5%)" }} />
                                            </div>
                                            <div className="relative h-[88%] w-[28%] -translate-x-[15%] rotate-[5deg]">
                                                <Image src="/images/chronelle/review-clean-v3.png" alt="Chronelle review workflow" fill sizes="13vw" className="object-contain drop-shadow-[0_16px_18px_rgba(0,0,0,0.5)]" style={{ clipPath: "inset(0.5% 0.8% round 9% / 4.5%)" }} />
                                            </div>
                                        </div>
                                    </div>
                                ) : product.type === "image" ? (
                                    <Image
                                        src={product.image}
                                        alt=""
                                        fill
                                        sizes="(max-width: 768px) 85vw, (max-width: 1024px) 45vw, 40vw"
                                        className={`${
                                            product.objectFit === "contain" ? "object-contain p-4" : "object-cover"
                                        } transition-transform duration-700 group-hover:scale-105`}
                                    />
                                ) : (
                                    <div className={`w-full h-full ${product.color} flex items-center justify-center`}>
                                        <span className="text-white text-6xl font-bold tracking-tighter">apnaQ</span>
                                    </div>
                                )}
                            </div>
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-baseline gap-2">
                                    <span className="opacity-40 text-sm font-medium">{product.displayId}/</span>
                                    <h3 className="text-xl md:text-2xl opacity-60 font-normal leading-tight">{product.title}</h3>
                                </div>
                                <div className="w-10 h-10 rounded-full border border-black/20 flex items-center justify-center shrink-0 group-hover:bg-black group-hover:text-white transition-colors duration-300">
                                    <ArrowUpRight size={18} />
                                </div>
                            </div>
                        </Link>
                    </motion.div>
                ))}

                {/* Spacer for right padding */}
                <div 
                    className="w-6 md:w-20 lg:w-40 shrink-0 transition-all duration-300"
                    style={{ width: leftSpacerWidth ? `${leftSpacerWidth}px` : undefined }}
                ></div>
            </div>

            {/* Pagination Dots */}
            <div className="px-6 md:px-20 lg:px-40 mt-4">
                <div className="container mx-auto flex gap-2 justify-start">
                    {products.map((_, idx) => (
                        <button
                            key={idx}
                            onClick={() => scrollToProduct(idx)}
                            className={`w-2 h-2 rounded-full transition-all duration-300 ${activeIndex === idx ? 'bg-current scale-125' : 'bg-current opacity-20 hover:opacity-50'}`}
                            aria-label={`Go to slide ${idx + 1}`}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}
